import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart drawer upsell pair:
 *
 * <gb-gift-box>    — premium gift box offer card, shown while the box is NOT in
 *                    the cart. Adds it via /cart/update.js with an absolute
 *                    quantity of 1, so it can never be added twice.
 * <gb-cart-upsell> — "you might also like" carousel at the bottom of the drawer.
 *                    Cards are rendered by gb-cart-upsell.liquid from the
 *                    curated `cart_upsell_products` theme setting; this element
 *                    only wires the + button and the scroll progress bar.
 *
 * Both funnel mutations through the standard CartLinesUpdateEvent promise, so
 * cart-items-component morphs the drawer and the header bubble updates.
 */

/**
 * @param {HTMLElement} sourceEl element the CartLinesUpdateEvent is dispatched from
 * @param {string | number} variantId
 * @param {number} quantity absolute quantity for the variant
 * @returns {Promise<void>} resolves after the cart update succeeds
 */
function updateCart(sourceEl, variantId, quantity) {
  const sectionsToUpdate = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionsToUpdate.add(el.dataset.sectionId);
    }
  });

  const body = JSON.stringify({
    updates: { [variantId]: quantity },
    sections: Array.from(sectionsToUpdate).join(','),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  sourceEl.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'add' : 'remove',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
    .then((response) => response.json())
    .then((cart) => {
      if (cart.errors || cart.status) {
        throw new Error(cart.message || cart.errors || 'Cart update failed');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'gb-cart-upsell',
          didError: false,
        },
      });
    })
    .catch((error) => {
      deferred.reject(error);
      sourceEl.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Cart update failed',
          code: 'INVALID',
        })
      );
      throw error;
    });
}

class GbGiftBox extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = event.target instanceof Element && event.target.closest('.gb-gift-box__add');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    // On success the drawer morph re-renders the summary without this offer card.
    updateCart(this, variantId, 1).catch(() => {
      button.disabled = false;
    });
  };
}

class GbCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // capture, because scroll doesn't bubble — and listening on the host rather
    // than on the track keeps this working after a drawer morph swaps children
    this.addEventListener('scroll', this.#onTrackScroll, { capture: true, passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scroll', this.#onTrackScroll, { capture: true });
  }

  /**
   * Slides the heading's dark underline segment along the light rule as the
   * carousel scrolls (0..1 exposed as --gb-upsell-progress).
   * @param {Event} event
   */
  #onTrackScroll = (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.classList.contains('gb-cart-upsell__track')) return;
    const range = track.scrollWidth - track.clientWidth;
    const progress = range > 0 ? track.scrollLeft / range : 0;
    this.style.setProperty('--gb-upsell-progress', String(Math.min(Math.max(progress, 0), 1)));
  };

  /**
   * Toggles one card's size row and collapses every other one.
   * @param {Element | null} sizes
   * @returns {boolean} whether the row ended up open
   */
  #toggleSizes(sizes) {
    for (const el of this.querySelectorAll('.gb-cart-upsell__sizes')) {
      if (el === sizes || !(el instanceof HTMLElement)) continue;
      el.hidden = true;
      el.closest('.gb-cart-upsell__card')?.querySelector('.gb-cart-upsell__plus')?.setAttribute('aria-expanded', 'false');
    }
    if (!(sizes instanceof HTMLElement)) return false;
    sizes.hidden = !sizes.hidden;
    return !sizes.hidden;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   */
  #add(button, variantId) {
    button.disabled = true;
    // On success the drawer morph re-renders the strip without this product,
    // since it is now in the cart.
    updateCart(this, variantId, 1)
      .then(() => {
        button.disabled = false;
      })
      .catch(() => {
        button.disabled = false;
      });
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const size = target.closest('.gb-cart-upsell__size');
    if (size instanceof HTMLButtonElement) {
      if (!size.disabled && size.dataset.variantId) this.#add(size, size.dataset.variantId);
      return;
    }

    const plus = target.closest('.gb-cart-upsell__plus');
    if (!(plus instanceof HTMLButtonElement)) return;

    // One available variant: blind-add it. Otherwise the card carries a size row.
    const variantId = plus.dataset.variantId;
    if (variantId) {
      this.#add(plus, variantId);
      return;
    }

    const sizes = plus.closest('.gb-cart-upsell__card')?.querySelector('.gb-cart-upsell__sizes') ?? null;
    plus.setAttribute('aria-expanded', this.#toggleSizes(sizes) ? 'true' : 'false');
  };
}

/**
 * Section ids of every cart-items-component on the page, so a cart mutation can
 * ask for their re-rendered HTML in the same round trip.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * Add/remove several variants in one /cart/update.js call.
 * @param {HTMLElement} sourceEl
 * @param {Record<string, number>} updates variantId -> absolute quantity
 * @param {Record<string, unknown>} [extra] further /cart/update.js fields sent in
 *   the same request — `discount` and `attributes` for the gift flow, so the
 *   code lands together with the lines it is meant to discount
 * @returns {Promise<any>} the Ajax cart, so callers can check what came back
 */
function updateCartMulti(sourceEl, updates, extra = {}) {
  const body = JSON.stringify({
    updates,
    ...extra,
    sections: cartSectionIds().join(','),
    sections_url: window.location.pathname,
  });
  const lines = Object.entries(updates).map(([id, q]) => ({ merchandiseId: String(id), quantity: q }));
  const adding = Object.values(updates).some((q) => q > 0);

  const deferred = CartLinesUpdateEvent.createPromise();
  sourceEl.dispatchEvent(
    new CartLinesUpdateEvent({
      action: adding ? 'add' : 'remove',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
    .then((response) => response.json())
    .then((cart) => {
      if (cart.errors || cart.status) throw new Error(cart.message || cart.errors || 'Cart update failed');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'gb-gift-bar',
          didError: false,
        },
      });
      return cart;
    })
    .catch((error) => {
      deferred.reject(error);
      sourceEl.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Cart update failed', code: 'INVALID' }));
      throw error;
    });
}

const GIFT_CODE_KEY = 'gb_gift_code';

/** Guards the URL claim so it runs once per page load. */
let giftCodeClaimed = false;

/** @returns {string} the campaign code remembered from an earlier page view */
function rememberedGiftCode() {
  try {
    return (localStorage.getItem(GIFT_CODE_KEY) || '').trim().toUpperCase();
  } catch {
    return '';
  }
}

/** @param {string} code */
function rememberGiftCode(code) {
  try {
    localStorage.setItem(GIFT_CODE_KEY, code);
  } catch {
    // private mode — the code still works for this page view
  }
}

/**
 * Writes cart attributes without touching a single line, then morphs whatever
 * cart sections came back. Deliberately not routed through CartLinesUpdateEvent:
 * no line changed, so the header count and the other line listeners have
 * nothing to react to.
 * @param {Record<string, string>} attributes
 * @returns {Promise<any>} the Ajax cart
 */
async function setCartAttributes(attributes) {
  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        attributes,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    })
  );

  const cart = await response.json();
  if (cart.errors || cart.status) throw new Error(cart.message || cart.errors || 'Cart update failed');

  for (const [id, html] of Object.entries(cart.sections || {})) {
    if (typeof html !== 'string') continue;
    const host = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
    // Awaited and caught per section: morphSection rejects when a section is
    // missing from the page, and a stale drawer is not worth failing the write
    // that already succeeded server-side.
    await morphSection(id, html, { mode: host?.closest('theme-drawer') ? 'hydration' : 'full' }).catch(() => {});
  }

  return cart;
}

/**
 * <gb-gift-code> — the campaign gate. Records the code as a `gift_code` cart
 * attribute, which is the only thing gb-gift-bar renders on.
 *
 * Two instances, told apart by data-context:
 *
 *   global — rendered from the layout on every page, invisible. Claims the code
 *            from the Klaviyo link's ?gift_code=, which is the path nearly every
 *            recipient takes.
 *   drawer — the visible "Have a gift code?" field, for a shopper who has the
 *            code but not the link (read the email on another device, say).
 *
 * The code is remembered in localStorage because an email click usually lands
 * on an empty cart, where there is nothing yet to hang an attribute on: the
 * write is retried on the first line update instead.
 */
class GbGiftCode extends HTMLElement {
  /** @type {string} */
  #code = '';

  connectedCallback() {
    this.#code = (this.dataset.code || '').trim().toUpperCase();
    if (!this.#code) return;

    if (this.dataset.context === 'drawer') {
      this.addEventListener('submit', this.#onSubmit);
      return;
    }

    this.#claim();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
  }

  #activate() {
    return setCartAttributes({ gift_code: this.#code });
  }

  /** True once the attribute is on the cart, so nothing needs writing. */
  get #active() {
    return this.dataset.active === 'true';
  }

  /**
   * Claims the code from the URL, or from a previous page view that could not
   * write it yet.
   */
  #claim() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get('gift_code') || '').trim().toUpperCase();

    if (fromUrl) {
      // Strip it whether or not it matched: a code has no business sitting in
      // the address bar to be copied into a screenshot, and a real one is
      // remembered elsewhere.
      params.delete('gift_code');
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      );
    }

    if ((fromUrl || rememberedGiftCode()) !== this.#code) return;

    rememberGiftCode(this.#code);
    // Stripping the param above still had to happen for a cart that already
    // carries the attribute; the write itself does not.
    if (giftCodeClaimed || this.#active) return;

    giftCodeClaimed = true;
    this.#activate().catch(() => {
      // Almost always an empty cart. #onLinesUpdate picks it up again.
      giftCodeClaimed = false;
    });
  }

  #onLinesUpdate = () => {
    if (giftCodeClaimed || this.#active) return;
    if (rememberedGiftCode() !== this.#code) return;

    giftCodeClaimed = true;
    this.#activate().catch(() => {
      giftCodeClaimed = false;
    });
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    event.preventDefault();

    const input = this.querySelector('input[name="gb_gift_code"]');
    const error = this.querySelector('.gb-giftcode__error');
    const submit = this.querySelector('.gb-giftcode__submit');
    if (!(input instanceof HTMLInputElement)) return;

    if (input.value.trim().toUpperCase() !== this.#code) {
      if (error instanceof HTMLElement) error.hidden = false;
      input.select();
      return;
    }

    if (error instanceof HTMLElement) error.hidden = true;
    if (submit instanceof HTMLButtonElement) submit.disabled = true;

    rememberGiftCode(this.#code);
    giftCodeClaimed = true;
    // On success the drawer morph swaps this field out for the gift bar.
    this.#activate().catch(() => {
      giftCodeClaimed = false;
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      if (error instanceof HTMLElement) error.hidden = false;
    });
  };
}

/**
 * <gb-gift-bar> — the "choose your free product" bar + size-picker modal that
 * sits above the cart items. Below threshold it just shows progress (rendered
 * by Liquid). At/over threshold the "Choose your size" button opens the modal;
 * picking a Gift Top size + Gift Bottom size and confirming adds both variants.
 * If the qualifying subtotal drops below threshold while the gift is in the
 * cart, data-autoremove lists the variants to pull so they never get charged.
 */
class GbGiftBar extends HTMLElement {
  /** gift product handle -> chosen variant id */
  #selected = new Map();

  connectedCallback() {
    this.#seedSelection();
    this.addEventListener('click', this.#onClick);
    this.#loadBox();
    // capture phase + stopPropagation so Escape closes ONLY this modal and does
    // not bubble to the cart drawer's own Escape handler (closing the drawer
    // while this nested dialog is open orphans its top-layer backdrop, which
    // then blocks every click on the page).
    document.addEventListener('keydown', this.#onKey, true);

    const autoremove = this.dataset.autoremove;
    if (autoremove) {
      const updates = {};
      autoremove.split(',').filter(Boolean).forEach((id) => (updates[id] = 0));
      if (Object.keys(updates).length) updateCartMulti(this, updates).catch(() => {});
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKey, true);
  }

  /**
   * The 3D gift box (bar icon + size picker) is its own bundle, loaded only
   * once a bar that shows it is on the page. <gb-gift-3d> upgrades in place
   * when it arrives; until then (and without WebGL) its poster shows.
   */
  #loadBox() {
    const src = this.dataset.box3d;
    if (!src || customElements.get('gb-gift-3d') || !this.querySelector('gb-gift-3d')) return;
    import(src).catch(() => {});
  }

  #onKey = (event) => {
    if (event.key !== 'Escape') return;
    const modal = this.querySelector('[data-gb-giftbar-modal]');
    if (modal instanceof HTMLDialogElement && modal.open) {
      // handle the close ourselves and keep the event from reaching the drawer
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#toggleModal(false);
    }
  };

  #toggleModal(open) {
    const modal = this.querySelector('[data-gb-giftbar-modal]');
    if (!(modal instanceof HTMLDialogElement)) return;
    if (open) {
      modal.classList.remove('is-closing');
      if (modal.open) return;
      modal.showModal();
      // Rise from the bottom edge (phones) or settle in (the centred card). Run
      // here, once the dialog is open and laid out: as a CSS animation starting
      // with the dialog itself it came in from the top. Closing stays in CSS.
      const panel = modal.querySelector('.gb-giftbar-modal__panel');
      if (panel && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const sheet = matchMedia('(max-width: 749px)').matches;
        panel.animate(
          sheet
            ? [{ transform: `translateY(${panel.offsetHeight}px)` }, { transform: 'translateY(0)' }]
            : [{ opacity: 0, transform: 'translateY(12px) scale(0.98)' }, { opacity: 1, transform: 'none' }],
          { duration: sheet ? 280 : 240, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
        );
      }
    } else if (modal.open && !modal.classList.contains('is-closing')) {
      // Slide the sheet back down (or fade the card) before closing: .is-closing
      // runs the exit animation in gb-gift-bar.liquid. The timeout covers
      // reduced motion (no animation, no animationend) and a morph that
      // replaces the panel mid-animation.
      const panel = modal.querySelector('.gb-giftbar-modal__panel');
      const finish = () => {
        modal.classList.remove('is-closing');
        if (modal.open) modal.close();
      };
      if (!panel || matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
      modal.classList.add('is-closing');
      panel.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 320);
    }
  }

  /**
   * The distinct gift products the modal is asking a size for — one per handle
   * in gb-gift-config, so this is 1 for a single body and 2 for the top+bottom
   * set without the JS knowing which campaign is running.
   * @returns {string[]}
   */
  #groups() {
    const groups = new Set();
    this.querySelectorAll('[data-gb-gift-group]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.gbGiftGroup) groups.add(el.dataset.gbGiftGroup);
    });
    return Array.from(groups);
  }

  /**
   * Picks up sizes Liquid marked as chosen up front: a gift product with no
   * real options renders a single hidden, pre-selected button so the shopper is
   * never asked to choose a "Default Title".
   */
  #seedSelection() {
    for (const button of this.querySelectorAll('[data-gb-gift-group].is-selected')) {
      if (!(button instanceof HTMLElement)) continue;
      const { gbGiftGroup: group, variantId } = button.dataset;
      if (group && variantId) this.#selected.set(group, variantId);
    }
    this.#refreshAdd();
  }

  #refreshAdd() {
    const add = this.querySelector('[data-gb-giftbar-add]');
    if (!(add instanceof HTMLButtonElement)) return;

    const groups = this.#groups();
    add.disabled = groups.length === 0 || groups.some((group) => !this.#selected.get(group));
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-gb-giftbar-open]')) {
      this.#toggleModal(true);
      return;
    }
    if (target.closest('[data-gb-giftbar-close]') || target.matches('[data-gb-giftbar-modal]')) {
      this.#toggleModal(false);
      return;
    }

    const size = target.closest('[data-gb-gift-group]');
    if (size instanceof HTMLButtonElement && !size.disabled) {
      const { gbGiftGroup: group, variantId } = size.dataset;
      if (!group || !variantId) return;
      this.querySelectorAll(`[data-gb-gift-group="${group}"]`).forEach((b) => b.classList.remove('is-selected'));
      size.classList.add('is-selected');
      this.#selected.set(group, variantId);
      this.#refreshAdd();
      return;
    }

    const add = target.closest('[data-gb-giftbar-add]');
    if (add instanceof HTMLButtonElement) {
      const groups = this.#groups();
      if (!groups.length || groups.some((group) => !this.#selected.get(group))) return;
      add.disabled = true;
      this.#addGift(add);
    }
  };

  /**
   * Adds one chosen variant per gift product and re-applies the campaign code
   * in the SAME request, so the discount has its gift lines to act on the
   * instant it is evaluated — a code applied while the cart holds nothing it
   * can discount is simply not applicable.
   *
   * Then it checks Shopify actually zeroed them. A gift line that comes back
   * priced means the discount declined this cart (code expired or deleted,
   * per-customer use already spent, the discount's customerGets quantity lower
   * than the number of gift products configured, a market whose converted
   * minimum sits above our threshold), so the pick is rolled back rather than
   * left for the shopper to pay full price for at checkout.
   *
   * @param {HTMLButtonElement} add
   */
  async #addGift(add) {
    const chosen = this.#groups().map((group) => this.#selected.get(group));
    if (!chosen.length || chosen.some((variantId) => !variantId)) return;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const variantId of chosen) {
      if (variantId) updates[variantId] = 1;
    }

    const code = this.dataset.code || '';
    const handles = (this.dataset.handles || '').split(',').filter(Boolean);
    const message = this.dataset.error || '';

    try {
      const cart = await updateCartMulti(this, updates, code ? { discount: code, attributes: { gift_code: code } } : {});

      const charged = (cart?.items || []).filter(
        (/** @type {{ handle: string, final_line_price: number }} */ item) =>
          handles.includes(item.handle) && item.final_line_price > 0
      );

      if (!charged.length) {
        this.#toggleModal(false);
        return;
      }

      await updateCartMulti(
        this,
        Object.fromEntries(charged.map((/** @type {{ variant_id: number }} */ item) => [item.variant_id, 0]))
      );
      GbGiftBar.showError(message);
    } catch {
      add.disabled = false;
    }
  }

  /**
   * Surfaces the rollback message on whichever gift bar the drawer ended up
   * with. Queried from the document on a later frame because the rollback
   * re-renders the drawer, which can replace the node this instance lives in.
   * @param {string} message
   */
  static showError(message) {
    if (!message) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const slot = document.querySelector('gb-gift-bar [data-gb-giftbar-error]');
        if (!(slot instanceof HTMLElement)) return;
        slot.textContent = message;
        slot.hidden = false;
      });
    });
  }
}

if (!customElements.get('gb-gift-box')) {
  customElements.define('gb-gift-box', GbGiftBox);
}
if (!customElements.get('gb-cart-upsell')) {
  customElements.define('gb-cart-upsell', GbCartUpsell);
}
if (!customElements.get('gb-gift-bar')) {
  customElements.define('gb-gift-bar', GbGiftBar);
}
if (!customElements.get('gb-gift-code')) {
  customElements.define('gb-gift-code', GbGiftCode);
}
